import {
	Box,
	Chip,
	Divider,
	FormControl,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
} from "@mui/material";
import { memo, useContext, useEffect, useState } from "react";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import { useTranslation } from "react-i18next";
import { Context } from "./Context";
import moment from "moment";
import { trackGaClick } from "../../../utils/analytics";

const ShiftCell = memo(({ params }) => {
	return (
		<Box>
			{params?.value?.map((shift, index) => (
				<Chip
					key={index}
					sx={{ width: "150px" }}
					color={"info"}
					icon={<WorkHistoryIcon sx={{ p: 0.25 }} />}
					label={
						<Box sx={{ width: "80px", textAlign: "left", ml: 2 }}>
							{`${moment(shift[0].start, "YYYY-MM-DDTHH:mm:ss").format("HH:mm")}-${moment(
								shift[shift.length - 1].end,
								"YYYY-MM-DDTHH:mm:ss",
							).format("HH:mm")}`}
						</Box>
					}
				/>
			))}
		</Box>
	);
});

const PunchCell = memo(({ params }) => {
	const { periods } = useContext(Context);

	const totalSpan = [...new Set(params.value.map((punch) => punch.total_span_index))];

	return totalSpan.map((total_index, index) => (
		<Box key={`${total_index}-${index}`}>
			<center>
				<Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={2}>
					{params.value
						.filter((punch) => punch.total_span_index === total_index)
						.map((punch, index) => (
							<Box key={index}>
								<Chip
									sx={{ width: "120px" }}
									color={"success"}
									icon={<AssignmentIndIcon sx={{ p: 0.25 }} />}
									label={<Box sx={{ width: "80px", textAlign: "left", ml: 2 }}>{punch?.time}</Box>}
									disabled={moment(
										periods.find((period) => period?.person_number === params?.row?.person_number)?.end_date,
										"YYYY-MM-DD",
									).isSameOrAfter(moment(params?.row?.date, "YYYY-MM-DD"))}
								/>
							</Box>
						))}
				</Stack>
			</center>
		</Box>
	));
});

const ActionCell = memo(({ params }) => {
	const { t } = useTranslation("overtime.table");

	const { apiRef, rows, periods } = useContext(Context);
	const [action, setAction] = useState(null);

	useEffect(() => {
		setAction(rows.find((row) => row.id === params.row.id)?.action);
	}, [params, rows]);

	return (
		<>
			<FormControl fullWidth>
				<InputLabel id="laction">{t("table.rows.action.title")}</InputLabel>

				<Select
					labelId="laction"
					id="action"
					value={action}
					label={t("table.rows.action.title")}
					onChange={(event) => {
						const selectedValue = event.target.value;

						trackGaClick({
							id: "overtime.select_action",
							gaAction: "select_overtime_action",
							gaType:
								selectedValue === "all"
									? "full"
									: selectedValue === "some"
									? "partial"
									: selectedValue === "none"
									? "reject"
									: "none",
							extra: {
								rowId: params.row.id,
								personNumber: params.row.person_number,
								date: params.row.date,
								status: params.row.status,
							},
						});

						setAction(selectedValue);

						let nrows = rows;

						for (const row of nrows) {
							if (row.id === params.row.id) {
								row.action = selectedValue;

								if (selectedValue === "all" || selectedValue === "some" || selectedValue === "none") {
									row.partial_amount = row.amount;
								} else {
									row.partial_amount = "00:00";
								}
							}
						}

						apiRef.current.setRows(nrows);
					}}
					disabled={
						params.row.status !== "pending" ||
						moment(
							periods.find((period) => period?.person_number === params?.row?.person_number)?.end_date,
							"YYYY-MM-DD",
						).isSameOrAfter(moment(params?.row?.date, "YYYY-MM-DD"))
					}
				>
					<MenuItem value={""}>{t("table.rows.action.no_action")}</MenuItem>
					<MenuItem value={"all"}>{t("table.rows.action.full_approval")}</MenuItem>
					<MenuItem value={"some"}>{t("table.rows.action.partial_approval")}</MenuItem>
					<MenuItem value={"none"}>{t("table.rows.action.reject_everything")}</MenuItem>
				</Select>
			</FormControl>
		</>
	);
});

const PartialCell = memo(({ params }) => {
	const { t } = useTranslation("overtime.table");

	const { rows, setRows, periods } = useContext(Context);

	const [partial, setPartial] = useState(
		rows.find((row) => row.id === params.row.id)?.partial_amount,
	);

	useEffect(() => {
		setPartial(rows.find((row) => row.id === params.row.id)?.partial_amount);
	}, [params, rows]);

	return (
		<>
			<TextField
				label={t("table.rows.partial.title")}
				value={partial}
				onChange={(event) => {
					setPartial(event.target.value);

					setRows((prev) => {
						for (const row of prev) {
							if (row.id === params.row.id) {
								row.partial_amount = event.target.value;
							}
						}

						return prev;
					});
				}}
				disabled={
					params.row.action !== "some" ||
					params.row.status !== "pending" ||
					moment(
						periods.find((period) => period?.person_number === params?.row?.person_number)?.end_date,
						"YYYY-MM-DD",
					).isSameOrAfter(moment(params?.row?.date, "YYYY-MM-DD"))
				}
				placeholder={t("table.rows.partial.placeholder")}
				error={!/^\d{1,2}:[0-5]\d$/.test(partial)}
				helperText={!/^\d{1,2}:[0-5]\d$/.test(partial) ? t("table.rows.partial.error") : ""}
				size="small"
				InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							<WatchLaterIcon />
						</InputAdornment>
					),
				}}
			/>
		</>
	);
});

const CommentCell = memo(({ params }) => {
	const { t } = useTranslation("overtime.table");

	const { apiRef, rows, comments, loadingComments, periods } = useContext(Context);
	const [comment, setComment] = useState("");

	useEffect(() => {
		setComment(rows.find((row) => row.id === params.row.id)?.comment || "");
	}, [params, rows]);

	return (
		<>
			<FormControl fullWidth>
				<InputLabel id="lcomment">{t("table.rows.comment.title")}</InputLabel>

				<Select
					labelId="lcomment"
					id="comment"
					value={comment}
					label={t("table.rows.comment.title")}
					disabled={
						loadingComments ||
						params.row.status !== "pending" ||
						moment(
							periods.find((period) => period?.person_number === params?.row?.person_number)?.end_date,
							"YYYY-MM-DD",
						).isSameOrAfter(moment(params?.row?.date, "YYYY-MM-DD"))
					}
					onChange={(event) => {
						const selectedValue = event.target.value;
						setComment(selectedValue);

						let nrows = rows;

						for (const row of nrows) {
							if (row.id === params.row.id) {
								row.comment = selectedValue;
							}
						}

						apiRef.current.setRows(nrows);
					}}
				>
					<MenuItem value="">{t("table.rows.comment.no_comment")}</MenuItem>
					{comments.map((commentOption) => (
						<MenuItem key={commentOption} value={commentOption}>
							{commentOption}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</>
	);
});

const NoteCell = memo(({ params }) => {
	const { t } = useTranslation("overtime.table");

	const { rows, setRows, periods } = useContext(Context);

	const [note, setNote] = useState(rows.find((row) => row.id === params.row.id)?.notes || "");

	useEffect(() => {
		setNote(rows.find((row) => row.id === params.row.id)?.notes || "");
	}, [params, rows]);

	return (
		<>
			<TextField
				label={t("table.rows.note.title")}
				value={note}
				onChange={(event) => {
					setNote(event.target.value);

					setRows((prev) => {
						for (const row of prev) {
							if (row.id === params.row.id) {
								row.notes = event.target.value;
							}
						}

						return prev;
					});
				}}
				disabled={
					params.row.status !== "pending" ||
					moment(
						periods.find((period) => period?.person_number === params?.row?.person_number)?.end_date,
						"YYYY-MM-DD",
					).isSameOrAfter(moment(params?.row?.date, "YYYY-MM-DD"))
				}
				placeholder={t("table.rows.note.placeholder")}
				multiline
				rows={2}
				size="small"
				fullWidth
			/>
		</>
	);
});

const Cell = ({ params }) => {
	switch (params?.field) {
		case "shifts":
			return <ShiftCell params={params} />;
		case "punches":
			return <PunchCell params={params} />;
		case "action":
			return <ActionCell params={params} />;
		case "parcial_amount":
			return <PartialCell params={params} />;
		case "comment":
			return <CommentCell params={params} />;
		case "note":
			return <NoteCell params={params} />;
		default:
	}
};

const renderCell = (params) => {
	return (
		<>
			<Box
				id={`${params?.field}-cell-${params?.row?.id}`}
				sx={{
					display: "grid",
					placeItems: "center",
					width: "100%",
					height: "100%",
					gap: "10px",
					p: 1.5,
				}}
			>
				<Cell params={params} />
			</Box>
		</>
	);
};

export { renderCell };
